import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import websocketService from "../services/websocketService";
import websocketScopeHandler from "../services/websocketScopeHandler";

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState("CLOSED");

  useEffect(() => {
    console.log("[WebSocketContext] Initializing WebSocket connection...");

    // Connect when component mounts
    websocketService.connect();

    // Initialize scope handler to parse and route messages by scope
    websocketScopeHandler.initialize();

    // Set up event listeners
    const handleOpen = () => {
      console.log("[WebSocketContext] Connection opened");
      setIsConnected(true);
      setConnectionState(websocketService.getState());
    };

    const handleClose = () => {
      console.log("[WebSocketContext] Connection closed");
      setIsConnected(false);
      setConnectionState(websocketService.getState());
    };

    const handleError = (error) => {
      console.error("[WebSocketContext] Connection error", error);
      setIsConnected(false);
      setConnectionState(websocketService.getState());
    };

    const handleMessage = (data) => {
      console.log("[WebSocketContext] Message received in context", data);
    };

    websocketService.on("open", handleOpen);
    websocketService.on("close", handleClose);
    websocketService.on("error", handleError);
    websocketService.on("message", handleMessage);

    // Update connection state periodically
    const stateInterval = setInterval(() => {
      setConnectionState(websocketService.getState());
      setIsConnected(websocketService.isReady());
    }, 1000);

    // Cleanup on unmount
    return () => {
      console.log("[WebSocketContext] Cleaning up WebSocket connection...");
      websocketScopeHandler.cleanup();
      websocketService.off("open", handleOpen);
      websocketService.off("close", handleClose);
      websocketService.off("error", handleError);
      websocketService.off("message", handleMessage);
      clearInterval(stateInterval);
      // Note: We don't disconnect here because we want the connection to persist
      // If you want to disconnect on unmount, uncomment the line below:
      // websocketService.disconnect();
    };
  }, []);

  const sendMessage = useCallback((data) => {
    console.log("[WebSocketContext] Sending message", data);
    return websocketService.send(data);
  }, []);

  const subscribe = useCallback((event, callback) => {
    console.log(`[WebSocketContext] Subscribing to event: ${event}`);
    websocketService.on(event, callback);
    return () => {
      console.log(`[WebSocketContext] Unsubscribing from event: ${event}`);
      websocketService.off(event, callback);
    };
  }, []);

  const value = {
    isConnected,
    connectionState,
    sendMessage,
    subscribe,
    websocketService,
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};

export default WebSocketContext;

